import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

/**
 * useConfirm — global promise-based confirmation dialog.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "¿Borrar estudiante?",
 *     description: "Se eliminarán sus matrículas y pagos. No se puede deshacer.",
 *     confirmText: "Sí, borrar",
 *     danger: true,
 *   });
 *   if (!ok) return;
 *   await api.delete(...);
 */
const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "¿Confirmar acción?",
  description: "",
  confirmText: "Confirmar",
  cancelText: "Cancelar",
  danger: false,
};

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULTS);
  const [checkboxValue, setCheckboxValue] = useState(false);
  const resolverRef = useRef(null);

  const confirm = useCallback((partial = {}) => {
    setOpts({ ...DEFAULTS, ...partial });
    setCheckboxValue(!!partial.checkbox?.defaultValue);
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const finish = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      // When a checkbox is defined we return an object so callers can read
      // its final state. Otherwise we keep the legacy boolean signature.
      const payload = opts.checkbox
        ? (result ? { ok: true, checkbox: checkboxValue } : { ok: false, checkbox: false })
        : result;
      resolverRef.current(payload);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(v) => (!v ? finish(false) : null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {opts.danger && <AlertTriangle className="h-5 w-5 text-red-500" />}
              {opts.title}
            </AlertDialogTitle>
            {opts.description && (
              <AlertDialogDescription className="whitespace-pre-line">
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {opts.checkbox && (
            <label className="flex items-start gap-2 px-1 py-2 text-sm cursor-pointer select-none" data-testid="confirm-checkbox-label">
              <input
                type="checkbox"
                checked={checkboxValue}
                onChange={(e) => setCheckboxValue(e.target.checked)}
                data-testid="confirm-checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-slate-700">{opts.checkbox.label}</span>
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-cancel-btn"
              onClick={() => finish(false)}
            >
              {opts.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-accept-btn"
              onClick={() => finish(true)}
              className={
                opts.danger
                  ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                  : ""
              }
            >
              {opts.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Silent fallback: if provider is missing, use native window.confirm so we
    // never break the app during migration.
    return async (opts = {}) => window.confirm(opts.title || "¿Confirmar?");
  }
  return ctx;
}
